import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, Layers, ShieldCheck, Zap } from 'lucide-react';
import { fetchProducts } from '../features/products/productsSlice';
import { getAssetUrl } from '../utils/urls';
import { getProductCardImage, isPublicCoverPath } from '../utils/productCovers';
import { translateProduct } from '../utils/productTranslations';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Newsletter from '../components/Newsletter';
import GuaranteePopup from '../components/GuaranteePopup';
import Seo from '../components/Seo';
import { getStaticMeta } from '../seo/pageMeta';
import { devError } from '../utils/logger';
import { formatDisplayPrice } from '../utils/currencyDisplay';
import { Button, SectionHeading } from '../components/ui';
import BrandLogo from '../components/BrandLogo';
import HeroChart from '../components/HeroChart';
import { COMING_SOON_ITEMS, UNLISTED_PRODUCT_SLUGS } from '../data/comingSoon';

const TRADING_STYLE_ICONS = [BarChart3, Layers, Zap];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const HomeScreen = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { products, loading, error, status } = useSelector((state) => state.products);
  const homeMeta = getStaticMeta('home', i18n.language);
  const catalogReady = status === 'succeeded' || status === 'failed';

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const pillars = [
    {
      title: t('home.tradingStyles.volumeAnalysis.title'),
      description: t('home.tradingStyles.volumeAnalysis.description'),
    },
    {
      title: t('home.tradingStyles.sessionLevels.title'),
      description: t('home.tradingStyles.sessionLevels.description'),
    },
    {
      title: t('home.tradingStyles.intradayTrading.title'),
      description: t('home.tradingStyles.intradayTrading.description'),
    },
  ];

  const trustItems = [
    {
      title: t('home.features.precision.title'),
      description: t('home.features.precision.description'),
    },
    {
      title: t('home.features.quality.title'),
      description: t('home.features.quality.description'),
    },
    {
      title: t('home.features.support.title'),
      description: t('home.features.support.description'),
    },
  ];

  return (
    <>
      <Seo
        title={homeMeta.title}
        description={homeMeta.description}
        path="/"
        ready={catalogReady}
      />
      {/* Hero — une composition full-bleed */}
      <section className="linear-atmosphere relative overflow-hidden pt-36 md:pt-44">
        <div className="linear-grain pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-page px-6">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mb-6 flex justify-center">
              <BrandLogo link={false} className="h-24 md:h-32" />
            </div>
            <h1 className="text-[2.4rem] font-semibold leading-[1.08] tracking-tight text-body sm:text-5xl md:text-[3.5rem]">
              {i18n.language === 'fr' ? (
                <>
                  Indicateurs premium
                  <span className="mt-2 block text-muted">pour NinjaTrader 8</span>
                </>
              ) : (
                <>
                  Premium indicators
                  <span className="mt-2 block text-muted">for NinjaTrader 8</span>
                </>
              )}
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-muted md:text-lg">
              {t('home.hero.description')}
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Button variant="primary" to="/indicators" className="px-6 py-3">
                {t('home.hero.cta')}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
              <Button variant="secondary" to="/ninjatrader" className="px-6 py-3">
                {t('home.hero.demo')}
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Fenêtre produit plus large que le reste de la page */}
        <div className="relative mx-auto mt-14 w-full max-w-[1200px] px-4 md:mt-16 md:px-6">
          <HeroChart />
        </div>
      </section>

      {/* Pillars */}
      <section className="border-t border-white/[.06] bg-bg py-24 md:py-section">
        <div className="mx-auto max-w-page px-6">
          <SectionHeading
            index={i18n.language === 'fr' ? 'Approche' : 'Approach'}
            title={
              i18n.language === 'fr'
                ? 'Des outils pour chaque style'
                : 'Tools for every style'
            }
            subtitle={t('home.tradingStyles.subtitle')}
          />

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-card border border-white/[.08] bg-white/[.06] md:grid-cols-3">
            {pillars.map((style, index) => {
              const Icon = TRADING_STYLE_ICONS[index];
              return (
                <motion.div
                  key={style.title}
                  className="bg-bg p-8 md:p-10"
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: index * 0.08 }}
                >
                  <Icon className="mb-5 h-5 w-5 text-primary" aria-hidden="true" />
                  <h3 className="mb-2 text-lg font-semibold text-body">{style.title}</h3>
                  <p className="text-sm leading-relaxed text-muted">{style.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="border-t border-white/[.06] bg-bg py-24 md:py-section">
        <div className="mx-auto max-w-page px-6">
          <div className="mb-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <SectionHeading
              index={i18n.language === 'fr' ? 'Catalogue' : 'Catalog'}
              title={
                i18n.language === 'fr' ? 'Indicateurs premium' : 'Premium indicators'
              }
              className="mb-0"
            />
            <Button variant="secondary" to="/indicators" className="shrink-0">
              {t('home.products.viewAll')}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>

          {loading ? (
            <Loader />
          ) : error ? (
            <Message variant="danger">{error}</Message>
          ) : (
            <div className="divide-y divide-white/[.06] border-y border-white/[.06]">
              {(Array.isArray(products) ? products : [])
                .filter((indicator) => indicator?.slug && !UNLISTED_PRODUCT_SLUGS.has(indicator.slug))
                .slice(0, 6)
                .map((indicator, index) => {
                  if (!indicator || !indicator._id) return null;
                  const translatedIndicator = translateProduct(indicator, i18n.language);
                  const cardImage = getProductCardImage(indicator);
                  const cardImageSrc = isPublicCoverPath(cardImage)
                    ? cardImage
                    : getAssetUrl(cardImage);
                  return (
                    <motion.article
                      key={indicator._id}
                      className="group grid grid-cols-1 gap-6 py-8 md:grid-cols-12 md:items-center md:gap-10 md:py-10"
                      {...fadeUp}
                      transition={{ ...fadeUp.transition, delay: index * 0.05 }}
                    >
                      <div className="overflow-hidden rounded-card border border-white/[.08] bg-surface md:col-span-5">
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={cardImageSrc}
                            alt={translatedIndicator.name}
                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                            onError={(e) => {
                              devError('Image load error:', {
                                src: e.target.src,
                                indicatorId: indicator._id,
                              });
                            }}
                          />
                        </div>
                      </div>

                      <div className="md:col-span-7">
                        <h3 className="text-xl font-semibold tracking-tight text-body md:text-2xl">
                          {translatedIndicator.name}
                        </h3>
                        <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted md:text-base">
                          {translatedIndicator.description}
                        </p>
                        <div className="mt-6 flex flex-wrap items-center gap-4">
                          <span className="text-lg font-medium text-body">
                            {translatedIndicator.price === 0
                              ? t('home.products.free')
                              : formatDisplayPrice(translatedIndicator.price, i18n.language)}
                          </span>
                          <Button
                            variant="secondary"
                            to={`/product/${translatedIndicator._id}`}
                          >
                            {t('home.products.viewProduct')}
                          </Button>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              {(Array.isArray(products) ? products : []).length === 0 && (
                <p className="py-10 text-muted">
                  {i18n.language === 'fr'
                    ? 'Aucun produit disponible pour le moment.'
                    : 'No products available at the moment.'}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Trust — Why choose Alturion */}
      <section className="border-t border-white/[.06] bg-surface py-24 md:py-section">
        <div className="mx-auto max-w-page px-6">
          <SectionHeading
            index={i18n.language === 'fr' ? 'Confiance' : 'Trust'}
            title={t('home.features.title')}
            subtitle={
              i18n.language === 'fr'
                ? 'Conçus pour NinjaTrader 8, avec une garantie satisfait ou remboursé.'
                : 'Built for NinjaTrader 8, with a risk-free money-back guarantee.'
            }
          />

          <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
            {trustItems.map((item, index) => (
              <motion.div
                key={item.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: index * 0.08 }}
              >
                <ShieldCheck className="mb-4 h-5 w-5 text-primary" aria-hidden="true" />
                <h3 className="mb-2 text-base font-semibold text-body">{item.title}</h3>
                <p className="text-sm leading-relaxed text-muted">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming soon */}
      <section className="border-t border-white/[.06] bg-bg py-24 md:py-section">
        <div className="mx-auto max-w-page px-6">
          <SectionHeading
            index={t('home.comingSoon.index')}
            title={t('home.comingSoon.title')}
            subtitle={t('home.comingSoon.subtitle')}
          />

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-card border border-white/[.08] bg-white/[.06] sm:grid-cols-2 lg:grid-cols-3">
            {COMING_SOON_ITEMS.map((item, index) => {
              const Icon = item.icon;
              const name = t(`home.comingSoon.items.${item.id}.name`);
              return (
                <motion.div
                  key={item.id}
                  className="flex flex-col bg-bg"
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: index * 0.06 }}
                >
                  {item.image ? (
                    <div className="aspect-[16/10] overflow-hidden border-b border-white/[.06] bg-surface">
                      <img
                        src={item.image}
                        alt={name}
                        className="h-full w-full object-cover object-center"
                        onError={(e) => {
                          devError('Coming soon image load error:', {
                            src: e.target.src,
                            id: item.id,
                          });
                        }}
                      />
                    </div>
                  ) : null}
                  <div className="flex flex-1 flex-col p-7 md:p-8">
                    <div
                      className={`mb-4 flex items-center gap-3 ${
                        item.image ? 'justify-end' : 'justify-between'
                      }`}
                    >
                      {!item.image && (
                        <Icon className="h-[18px] w-[18px] text-primary" aria-hidden="true" />
                      )}
                      <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted">
                        {t('home.comingSoon.badge')}
                      </span>
                    </div>
                    <h3 className="mb-2 text-base font-semibold text-body">{name}</h3>
                    <p className="text-sm leading-relaxed text-muted">
                      {t(`home.comingSoon.items.${item.id}.teaser`)}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/[.06] bg-bg py-24 md:py-section">
        <div className="mx-auto max-w-page px-6 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-[28px] font-semibold tracking-tight text-body md:text-[40px]">
              {i18n.language === 'fr'
                ? 'Prêt à élever votre trading ?'
                : 'Ready to elevate your trading?'}
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-muted">{t('home.cta.subtitle')}</p>
            <div className="mt-8 flex justify-center">
              <Button variant="primary" to="/indicators" className="px-7 py-3">
                {t('home.cta.button')}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Newsletter />
      <GuaranteePopup />
    </>
  );
};

export default HomeScreen;
